/** Membership commands and durable display data; ledger rows remain in their own store. */
export { formatFamilyCode } from '../../shared/family_limits';
export { FAMILY_MEMBER_LIMIT as FAMILY_LIMIT } from '../../shared/family_limits';
import { createFamily, getFamily, joinFamily, validateFamilyResponse, type FamilyCredential, type FamilyView } from './backend';
import { db, familyDb, getMeta, setMeta } from './db';
import { familyLedger } from './ledger';
import { seedFamilyFallbackIfEmpty, updateProfile, clearGridOrderIfSet } from './repo';
import { FAMILY_ID_KEY, FAMILY_VIEW_KEY, FAMILY_REMOVED_KEY, resolveEmptyRemoval, queueSync } from './sync';

export async function cacheFamily(family: FamilyView | null): Promise<void> {
  if (family) {
    (await import("./family_notice")).dismissFamilyDeletion();
    await setMeta(FAMILY_VIEW_KEY, JSON.stringify(family));
    await setMeta(FAMILY_ID_KEY, family.family_id);
  }
}
export async function refreshFamily(): Promise<FamilyView | null> {
  const family = await getFamily();
  await cacheFamily(family);
  return family;
}
export async function createFamilyLedger(name: string, displayName: string, defaults: Parameters<typeof updateProfile>[0]): Promise<FamilyView> {
  await prepareFamilyMembership();
  const response = await createFamily(name.trim(), displayName.trim());
  if (!response.family) throw new Error('The server did not return the new family. Try again.');
  const ledger = familyLedger(response.family.family_id);
  await updateProfile(defaults, ledger);
  await seedFamilyFallbackIfEmpty(ledger);
  await cacheFamily(response.family);
  queueSync();
  return response.family;
}
async function prepareFamilyMembership(): Promise<void> {
  const removed = await familyDb.meta.get(FAMILY_REMOVED_KEY);
  if (removed && !await resolveEmptyRemoval(removed.value)) {
    throw new Error('Resolve your unsent family changes in Settings › Family before joining another family.');
  }
}
export async function joinFamilyLedger(credential: FamilyCredential, displayName: string): Promise<FamilyView> {
  await prepareFamilyMembership();
  const response = await joinFamily(credential, displayName.trim());
  if (!response.family) throw new Error('The server did not return your family. Try again.');
  await cacheFamily(response.family);
  queueSync();
  return response.family;
}
export async function cachedFamily(): Promise<FamilyView | null> {
  const value = await getMeta(FAMILY_VIEW_KEY);
  return value ? parseFamilyView(JSON.parse(value)) : null;
}
export async function familyExpenseCount(): Promise<number> {
  return familyDb.expenses.filter(row => row.deletedAt === null).count();
}
export async function clearFamilyView(): Promise<void> {
  await clearGridOrderIfSet();
  await db.meta.delete(FAMILY_VIEW_KEY); // invariant-exempt: INV-5 — local display cache, never a synced row
}
export function activeMembers(family: FamilyView): FamilyView['members'] { return family.members.filter(member => member.left_at === null); }
export function nextFamilyOwner(family: FamilyView): FamilyView['members'][number] | undefined {
  return activeMembers(family).filter(member => member.member_id !== family.my_member_id)
    .sort((a, b) => a.joined_at.localeCompare(b.joined_at) || a.member_id.localeCompare(b.member_id))[0];
}

export function parseFamilyView(value: unknown): FamilyView {
  validateFamilyResponse({ family: value }, "/", "GET");
  if (!value) throw new Error("Missing family membership.");
  return value as FamilyView;
}
